import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import useReveal from "../hooks/useReveal";
import { Loader2, Send, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "Inspektion",
  "Reparatur",
  "TÜV / HU",
  "Reifenservice",
  "Ölwechsel",
  "Diagnose",
  "Klimaservice",
  "Automatik-Getriebespülung",
  "KFZ-Gutachten",
  "Oldtimer-Reparatur",
  "Auto-Aufbereitung",
  "Sonstiges",
];

// 08:00 – 17:30 in 30-Min-Schritten
const TIMES = (() => {
  const out = [];
  for (let h = 8; h <= 17; h++) {
    out.push(`${String(h).padStart(2, "0")}:00`);
    out.push(`${String(h).padStart(2, "0")}:30`);
  }
  return out;
})();

const initial = {
  name: "",
  email: "",
  phone: "",
  service: "Inspektion",
  preferred_date: "",
  preferred_time: "09:00",
  vehicle: "",
  message: "",
  vehicle_registration: "", // base64 image (optional, new customers)
  vehicle_registration_name: "",
};

export default function Appointment() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [sectionRef, visible] = useReveal();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.phone || !form.preferred_date || !form.preferred_time) {
      toast.error("Bitte alle Pflichtfelder ausfüllen.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        preferred_date: `${form.preferred_date} ${form.preferred_time}`,
        vehicle: form.vehicle,
        message: form.message,
        vehicle_registration: form.vehicle_registration || "",
        vehicle_registration_name: form.vehicle_registration_name || "",
      };
      await axios.post(`${API}/appointments`, payload);
      setDone(true);
      toast.success("Terminanfrage gesendet. Wir melden uns zeitnah!");
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Senden fehlgeschlagen. Bitte versuchen Sie es erneut.";
      toast.error(typeof msg === "string" ? msg : "Senden fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 4 MB).");
      return;
    }
    if (!/^image\/(jpe?g|png|webp|heic|heif)$/i.test(file.type) && !file.name.match(/\.(jpe?g|png|webp|heic|heif)$/i)) {
      toast.error("Bitte ein Bild hochladen (JPG, PNG, WEBP).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({
        ...f,
        vehicle_registration: String(reader.result || ""),
        vehicle_registration_name: file.name,
      }));
      toast.success("Fahrzeugschein angehängt.");
    };
    reader.onerror = () => toast.error("Datei konnte nicht gelesen werden.");
    reader.readAsDataURL(file);
  };

  const removeFile = () =>
    setForm((f) => ({ ...f, vehicle_registration: "", vehicle_registration_name: "" }));

  const inputClass =
    "w-full bg-[#0f0f12] border border-zinc-800 text-white placeholder:text-zinc-600 px-4 py-3 text-sm focus:outline-none focus:border-[#E0B33D] focus:ring-1 focus:ring-[#E0B33D] rounded-none transition-colors";
  const labelClass =
    "block text-[10px] tracking-[0.3em] uppercase text-zinc-400 mb-2";

  return (
    <section
      id="termin"
      ref={sectionRef}
      data-testid="appointment-section"
      className={`relative bg-black border-t border-zinc-900 transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12 py-24 md:py-32 grid lg:grid-cols-12 gap-12">
        {/* Left */}
        <div className="lg:col-span-5">
          <div className="text-[11px] tracking-[0.4em] uppercase gold-text">
            — Terminanfrage
          </div>
          <h2
            data-testid="appointment-heading"
            className="font-display mt-4 text-4xl md:text-6xl uppercase tracking-tight leading-[0.95]"
          >
              Schnell.
              <br />
              Einfach.
              <br />
              <span className="gold-text">Unverbindlich.</span>
          </h2>
          <p className="mt-6 text-zinc-300 text-[15px] leading-relaxed max-w-md">
            Füllen Sie das Formular aus — wir melden uns persönlich zur
            Terminbestätigung. Alternativ sind wir jederzeit telefonisch für Sie
            da.
          </p>

          <div className="mt-10 space-y-4">
            <InfoRow label="Telefon" value="02131 544154" href="tel:02131544154" />
            <InfoRow label="Adresse" value="Normannenstraße 22, 41462 Neuss" />
            <InfoRow
              label="Öffnungszeiten"
              value="Mo–Fr · bis 18:00 Uhr"
            />
          </div>
        </div>

        {/* Form */}
        <form
          data-testid="appointment-form"
          onSubmit={submit}
          className="lg:col-span-7 bg-[#0f0f12] border border-zinc-800 p-6 md:p-10"
        >
          {done ? (
            <div
              data-testid="appointment-success"
              className="min-h-[400px] flex flex-col items-center justify-center text-center"
            >
              <CheckCircle2 className="gold-text" size={56} />
              <h3 className="font-display mt-6 text-3xl md:text-4xl uppercase tracking-tight">
                Anfrage gesendet
              </h3>
              <p className="mt-3 text-zinc-400 max-w-md">
                Vielen Dank! Wir haben Ihre Terminanfrage erhalten und melden
                uns in Kürze zur Bestätigung.
              </p>
              <button
                data-testid="appointment-send-another"
                onClick={() => setDone(false)}
                className="mt-8 border border-zinc-700 hover:border-[#E0B33D] hover:gold-text text-white px-6 py-3 text-xs tracking-[0.25em] uppercase transition-colors"
              >
                Weitere Anfrage
              </button>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Name *">
                  <input
                    data-testid="form-name"
                    className={inputClass}
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Max Mustermann"
                    required
                  />
                </Field>
                <Field label="E-Mail *">
                  <input
                    data-testid="form-email"
                    type="email"
                    className={inputClass}
                    value={form.email}
                    onChange={update("email")}
                    placeholder="max@example.de"
                    required
                  />
                </Field>
                <Field label="Telefon *">
                  <input
                    data-testid="form-phone"
                    className={inputClass}
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="0151 12345678"
                    required
                  />
                </Field>
                <Field label="Fahrzeug (optional)">
                  <input
                    data-testid="form-vehicle"
                    className={inputClass}
                    value={form.vehicle}
                    onChange={update("vehicle")}
                    placeholder="z. B. VW Golf 7, 2015"
                  />
                </Field>
                <Field label="Leistung *">
                  <select
                    data-testid="form-service"
                    className={inputClass}
                    value={form.service}
                    onChange={update("service")}
                  >
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Wunschtermin (Datum) *">
                  <input
                    data-testid="form-date"
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    className={inputClass}
                    value={form.preferred_date}
                    onChange={update("preferred_date")}
                    required
                  />
                </Field>
                <Field label="Wunschuhrzeit *">
                  <select
                    data-testid="form-time"
                    className={inputClass}
                    value={form.preferred_time}
                    onChange={update("preferred_time")}
                    required
                  >
                    {TIMES.map((t) => (
                      <option key={t} value={t}>
                        {t} Uhr
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="mt-5">
                <label className={labelClass}>Nachricht (optional)</label>
                <textarea
                  data-testid="form-message"
                  className={`${inputClass} min-h-[120px] resize-y`}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Beschreiben Sie kurz Ihr Anliegen …"
                />
              </div>

              {/* Fahrzeugschein-Upload (Neukunden) */}
              <div className="mt-5 border border-dashed border-zinc-800 bg-[#0c0c0e] p-5">
                <label className={labelClass}>
                  Fahrzeugschein-Foto (optional · für Neukunden)
                </label>
                {form.vehicle_registration ? (
                  <div className="flex items-center gap-4">
                    <img
                      src={form.vehicle_registration}
                      alt="Fahrzeugschein Vorschau"
                      className="w-20 h-20 object-cover border border-zinc-700"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">
                        {form.vehicle_registration_name || "Bild angehängt"}
                      </div>
                      <button
                        type="button"
                        onClick={removeFile}
                        data-testid="remove-vehicle-reg"
                        className="mt-1 text-[11px] tracking-[0.25em] uppercase text-zinc-400 hover:text-red-400"
                      >
                        Entfernen
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label
                      htmlFor="vehicle-reg-input"
                      className="inline-flex items-center gap-2 cursor-pointer border border-zinc-700 hover:border-[#E0B33D] hover:gold-text text-zinc-300 px-4 py-2.5 text-[11px] tracking-[0.25em] uppercase transition-colors"
                      data-testid="vehicle-reg-label"
                    >
                      Bild auswählen
                    </label>
                    <input
                      id="vehicle-reg-input"
                      data-testid="form-vehicle-registration"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleFile}
                      className="hidden"
                    />
                  </div>
                )}
                <p className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
                  Sind Sie zum ersten Mal bei uns? Foto vom Fahrzeugschein hilft
                  uns bei der Vorbereitung. <strong className="text-zinc-300">Bitte bringen Sie den Original-Fahrzeugschein zum Termin mit.</strong>
                </p>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-[11px] text-zinc-500 max-w-md leading-relaxed">
                  Mit Absenden stimmen Sie der Kontaktaufnahme zur
                  Terminabstimmung zu. Ihre Daten werden ausschließlich hierfür
                  verwendet.
                </p>
                <button
                  data-testid="form-submit"
                  type="submit"
                  disabled={submitting}
                  className="group inline-flex items-center gap-3 gold-bg disabled:opacity-60 text-black font-semibold px-6 py-3.5 text-xs tracking-[0.3em] uppercase transition-colors"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Senden …
                    </>
                  ) : (
                    <>
                      Anfrage senden <Send size={16} />
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-400 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}

function InfoRow({ label, value, href }) {
  const Inner = (
    <>
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
        {label}
      </div>
      <div className="mt-1 text-white text-base font-medium">{value}</div>
    </>
  );
  return (
    <div className="border-l-2 border-[#E0B33D] pl-4">
      {href ? (
        <a
          href={href}
          data-testid={`info-link-${label.toLowerCase()}`}
          className="block hover:gold-text transition-colors"
        >
          {Inner}
        </a>
      ) : (
        Inner
      )}
    </div>
  );
}
